import React, { useState } from 'react';
import { Users, Plus, Trash2, AlertCircle, Check, X, Clock, Mail, Key } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useWines } from '@/contexts/WinesContext';
import { useAuth } from '@/contexts/AuthContext';
import { GenerateConnectionCodeDialog } from './GenerateConnectionCodeDialog';
import { ConfirmAcceptInviteDialog } from './ConfirmAcceptInviteDialog';
import { DeleteConfirmModal } from '@/components/admin/DeleteConfirmModal';
import type { UserConnection } from '@/types';

export const ConnectionsManager: React.FC = () => {
  const { user } = useAuth();
  const {
    connectedUsers,
    sentInvites,
    receivedInvites,
    removeConnection,
    acceptInvite,
    rejectInvite,
    cancelInvite,
  } = useWines();

  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [inviteToAccept, setInviteToAccept] = useState<UserConnection | null>(null);
  const [connectionToRemove, setConnectionToRemove] = useState<string | null>(null);
  const [inviteToReject, setInviteToReject] = useState<string | null>(null);
  const [inviteToCancel, setInviteToCancel] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  if (!user) return null;

  const handleRemoveConnection = async () => {
    if (!connectionToRemove) return;

    try {
      setRemoving(true);
      await removeConnection(connectionToRemove);
      setConnectionToRemove(null);
    } catch (error) {
      console.error('Error removing connection:', error);
      alert('Errore durante la rimozione della connessione');
    } finally {
      setRemoving(false);
    }
  };

  const handleAcceptInvite = async (connectionId: string) => {
    await acceptInvite(connectionId);
  };

  const handleRejectInvite = async () => {
    if (!inviteToReject) return;

    try {
      setRemoving(true);
      await rejectInvite(inviteToReject);
      setInviteToReject(null);
    } catch (error) {
      console.error('Error rejecting invite:', error);
      alert('Errore durante il rifiuto dell\'invito');
    } finally {
      setRemoving(false);
    }
  };

  const handleCancelInvite = async () => {
    if (!inviteToCancel) return;

    try {
      setRemoving(true);
      await cancelInvite(inviteToCancel);
      setInviteToCancel(null);
    } catch (error) {
      console.error('Error cancelling invite:', error);
      alert('Errore durante la cancellazione dell\'invito');
    } finally {
      setRemoving(false);
    }
  };

  // Helper per ottenere info dell'altro utente nella connessione
  const getOtherUserInfo = (connection: any) => {
    const isUserA = connection.userA === user.id;
    return {
      id: isUserA ? connection.userB : connection.userA,
      name: isUserA ? connection.userBName : connection.userAName,
      email: isUserA ? connection.userBEmail : connection.userAEmail,
    };
  };

  const formatExpiryDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: 'short',
    }).format(date);
  };

  const isExpired = (timestamp: any) => {
    if (!timestamp) return false;
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date < new Date();
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Gruppo Condivisione
            </CardTitle>
            <span className="text-sm text-gray-600">
              {connectedUsers.length} {connectedUsers.length === 1 ? 'connessione' : 'connessioni'}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Info Box */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-900 font-medium mb-1">
                    Come funziona?
                  </p>
                  <p className="text-sm text-blue-800">
                    Crea connessioni permanenti con altri utenti per condividere facilmente i tuoi vini.
                    Genera un codice invito o accetta un codice ricevuto per iniziare.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center">
              <Button
                variant="outline"
                icon={<Plus className="w-5 h-5" />}
                onClick={() => setShowGenerateDialog(true)}
                className="w-full sm:w-auto"
              >
                Invita Utente
              </Button>
            </div>

            {/* Inviti Ricevuti */}
            {receivedInvites.length > 0 && (
              <div className="space-y-2 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Inviti Ricevuti ({receivedInvites.length})
                </h4>
                {receivedInvites.map((invite) => {
                  const expired = isExpired(invite.expiresAt);
                  return (
                    <div
                      key={invite.id}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        expired
                          ? 'bg-gray-50 border-gray-200'
                          : 'bg-green-50 border-green-200 hover:bg-green-100'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className="w-10 h-10 rounded-full bg-wine-purple/10 flex items-center justify-center flex-shrink-0">
                            <Users className="w-5 h-5 text-wine-purple" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 truncate">
                              {invite.userAName || 'Utente'}
                            </p>
                            <p className="text-sm text-gray-600 truncate">
                              {invite.userAEmail}
                            </p>
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {expired ? (
                                <span className="text-red-600 font-medium">Scaduto</span>
                              ) : (
                                <>Scade il {formatExpiryDate(invite.expiresAt)}</>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          {!expired && (
                            <>
                              <button
                                onClick={() => setInviteToAccept(invite)}
                                className="p-2 text-green-600 hover:text-green-700 hover:bg-green-100 rounded-lg transition-colors"
                                title="Accetta invito"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setInviteToReject(invite.id)}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                title="Rifiuta invito"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {expired && (
                            <button
                              onClick={() => setInviteToReject(invite.id)}
                              className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Rimuovi invito scaduto"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Inviti Inviati */}
            {sentInvites.length > 0 && (
              <div className="space-y-2 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Inviti Inviati ({sentInvites.length})
                </h4>
                {sentInvites.map((invite) => {
                  const expired = isExpired(invite.expiresAt);
                  return (
                    <div
                      key={invite.id}
                      className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                        expired ? 'bg-gray-50' : 'bg-yellow-50'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-full bg-wine-purple/10 flex items-center justify-center flex-shrink-0">
                          <Mail className="w-5 h-5 text-wine-purple" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 truncate">
                            {invite.inviteeEmail}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <Key className="w-3 h-3" />
                              Codice: <span className="font-mono font-medium">{invite.code}</span>
                            </p>
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {expired ? (
                                <span className="text-red-600 font-medium">Scaduto</span>
                              ) : (
                                <>Scade il {formatExpiryDate(invite.expiresAt)}</>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setInviteToCancel(invite.id)}
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        title="Cancella invito"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Connections List */}
            {connectedUsers.length > 0 && (
              <div className="space-y-2 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Utenti Connessi ({connectedUsers.length})
                </h4>
                {connectedUsers.map((connection) => {
                  const otherUser = getOtherUserInfo(connection);
                  return (
                    <div
                      key={connection.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-full bg-wine-purple/10 flex items-center justify-center flex-shrink-0">
                          <Users className="w-5 h-5 text-wine-purple" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 truncate">
                            {otherUser.name || 'Utente'}
                          </p>
                          <p className="text-sm text-gray-600 truncate">
                            {otherUser.email}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setConnectionToRemove(connection.id)}
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        title="Rimuovi connessione"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty State */}
            {connectedUsers.length === 0 && sentInvites.length === 0 && receivedInvites.length === 0 && (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-1">Nessuna connessione o invito</p>
                <p className="text-xs text-gray-500">
                  Inizia invitando un utente o accettando un invito
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <GenerateConnectionCodeDialog
        isOpen={showGenerateDialog}
        onClose={() => setShowGenerateDialog(false)}
      />

      <ConfirmAcceptInviteDialog
        isOpen={inviteToAccept !== null}
        onClose={() => setInviteToAccept(null)}
        invite={inviteToAccept}
        onAccept={handleAcceptInvite}
      />

      <DeleteConfirmModal
        isOpen={connectionToRemove !== null}
        onClose={() => setConnectionToRemove(null)}
        onConfirm={handleRemoveConnection}
        title="Rimuovi Connessione"
        message="Rimuovendo questa connessione, verranno eliminate anche tutte le condivisioni di vini tra voi due. Questa azione non può essere annullata."
        itemName="questa connessione"
        loading={removing}
      />

      <DeleteConfirmModal
        isOpen={inviteToReject !== null}
        onClose={() => setInviteToReject(null)}
        onConfirm={handleRejectInvite}
        title="Rifiuta Invito"
        message="Sei sicuro di voler rifiutare questo invito? Questa azione non può essere annullata."
        itemName="questo invito"
        loading={removing}
      />

      <DeleteConfirmModal
        isOpen={inviteToCancel !== null}
        onClose={() => setInviteToCancel(null)}
        onConfirm={handleCancelInvite}
        title="Cancella Invito"
        message="Sei sicuro di voler cancellare questo invito? L'utente invitato non potrà più accettarlo."
        itemName="questo invito"
        loading={removing}
      />
    </>
  );
};
